import { useEffect, useRef, useState } from 'react';
import type { Chat } from '@/data/types';
import { useChats } from '@/data/store';
import { supabase } from '@/lib/supabase';
import { DrcAvatar } from './Avatar';
import { AiLeo } from '@/components/AiLeo';

// "Leo's draft" on an email chat (Gary, 19 Oct): the customer Leo, who knows our prices, areas and the Science page,
// drafts an email reply and never sends it. A person reads it, edits it here, puts it in the message box and sends.
// Same shape as "Suggest a reply" (DrcSuggest), which is the staff assistant and has no prices.

function sampleDraft(firstName: string): string {
  return [
    `Hi ${firstName},`,
    '',
    'Thanks for getting in touch. We deep clean a 3-seater couch from R650 and armchairs from R250 each, in your home, and everything is dry within a few hours.',
    '',
    'Would you like us to find you a time in Rondebosch this week?',
    '',
    'Kind regards,',
    'CleanMyBed',
  ].join('\n');
}

export function EmailDraftSheet({ chat, onUse, onClose }: { chat: Chat; onUse: (text: string) => void; onClose: () => void }) {
  const { live } = useChats();
  const [draft, setDraft] = useState<string | null>(null);
  const [handover, setHandover] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const asked = useRef(false);

  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    if (!live) { setDraft(sampleDraft(chat.name.split(' ')[0] ?? chat.name)); return; }
    // WhatsApp chats get Leo's reply too (Gary, 19 Oct: the staff assistant's suggestions were poor).
    void supabase.functions.invoke('messaging-send', { body: { action: chat.email ? 'email_draft' : 'leo_draft', conversation_id: chat.id } })
      .then(({ data, error: fnError }) => {
        if (fnError || !data) { setError('Leo could not draft this one. Write the reply yourself.'); return; }
        const d = data as { draft?: string; handover?: { reason?: string; summary?: string }; error?: string };
        if (d.draft) setDraft(d.draft);
        else if (d.handover) setHandover([d.handover.reason, d.handover.summary].filter(Boolean).join('. ') || 'a person should answer this');
        else setError(d.error ?? 'Leo could not draft this one. Write the reply yourself.');
      });
  }, [live, chat]);

  const busy = draft === null && !handover && !error;
  return (
    <div className={chat.brand === 'pos' ? 'chats-brand-pos' : undefined}>
      <div className="chats-drc-head">
        <DrcAvatar large who="leo" />
        <div>
          <h2 className="chats-sheet-title"><AiLeo suffix="'s draft" /></h2>
          <p className="chats-sheet-hint">Only you see this. Check it, edit it here, then put it in your message box.</p>
        </div>
      </div>
      {busy && <span className="chats-drc-step">{chat.email ? 'Leo is writing the email...' : 'Leo is writing a reply...'}</span>}
      {error && <span className="chats-drc-error">{error}</span>}
      {handover && <p className="chats-sheet-hint">Leo would pass this one to a person: {handover}</p>}
      {draft !== null && (
        <textarea
          className="chats-input chats-suggest-draft"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={9}
          aria-label="Leo's draft, edit before using"
        />
      )}
      <button type="button" className="chats-btn" disabled={!draft?.trim()} onClick={() => { if (draft?.trim()) onUse(draft.trim()); }}>Put in message box</button>
      <button type="button" className="chats-btn chats-btn--ghost chats-suggest-later" onClick={onClose}>Not now</button>
    </div>
  );
}
